import { destinationPath } from "@travily/routes";
import type DestinationsCard from "@travily/types/models/DestinationsCard";
import type { DestinationsCardPhotoTrait } from "@travily/types/models/DestinationsCard";
import type { RankingExplanation } from "@travily/types/pending/models/DestinationSearchHit";
import { Card, CardDescription, CardFavoriteButton, CardImage, CardTag, CardTitle } from "@ui/components/Card";
import type { CardTheme } from "@ui/types/card";
import { RankExplanation } from "./RankExplanation";
import { type CostEvidenceGrade, isEstimatedCost, resolveDestinationPriceNote } from "./resolve-destination";

export type WeatherIcon = "sun" | "cloud";

function SunIcon() {
  return (
    <svg
      aria-hidden="true"
      className="size-5 shrink-0"
      fill="none"
      viewBox="0 0 20 20"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M9.27647 14.1034C11.5427 14.5029 13.7038 12.9897 14.1034 10.7235C14.503 8.45729 12.9898 6.29622 10.7235 5.89662C8.45731 5.49703 6.29623 7.01023 5.89664 9.27645C5.49704 11.5427 7.01024 13.7038 9.27647 14.1034Z"
        fill="#FFE29A"
      />
      <path
        d="M11.4471 1.79326L11.1577 3.4346M8.84235 16.5654L8.55293 18.2067M3.43462 8.84233L1.79327 8.55292M6.15654 4.51095L5.20058 3.1457M15.489 6.15652L16.8543 5.20056M4.51037 13.8468L3.14512 14.8028M13.8429 15.4924L14.7988 16.8577M18.2067 11.4471L16.5654 11.1576M14.1034 10.7235C13.7038 12.9897 11.5427 14.5029 9.27647 14.1034C7.01024 13.7038 5.49704 11.5427 5.89664 9.27645C6.29623 7.01023 8.45731 5.49703 10.7235 5.89662C12.9898 6.29622 14.503 8.4573 14.1034 10.7235Z"
        stroke="#B87C1F"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.5"
      />
    </svg>
  );
}

function CloudRainingIcon() {
  return (
    <svg
      aria-hidden="true"
      className="size-5 shrink-0"
      fill="none"
      viewBox="0 0 20 20"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M5.39015 11.7321C3.12392 11.3325 1.61072 9.17145 2.01031 6.90523C2.40991 4.639 4.57099 3.1258 6.83722 3.5254C6.86442 3.53019 6.89151 3.53524 6.9185 3.54055C7.92323 2.21034 9.62033 1.47757 11.3747 1.78691C13.4422 2.15146 14.9399 3.84879 15.1401 5.83952C15.1965 5.84686 15.253 5.85551 15.3096 5.86549C17.3492 6.22513 18.7111 8.1701 18.3514 10.2097C17.9918 12.2493 16.0468 13.6112 14.0072 13.2515C10.9243 12.7079 8.86587 12.345 5.39015 11.7321Z"
        fill="#C1DDFF"
      />
      <path
        d="M12.2697 15.9068L11.0873 17.8138M5.70435 14.7492L4.52191 16.6562M8.98704 15.328L7.8046 17.235M5.39015 11.7321C3.12392 11.3325 1.61072 9.17145 2.01031 6.90523C2.40991 4.639 4.57099 3.1258 6.83722 3.5254C6.86442 3.53019 6.89151 3.53524 6.9185 3.54055C7.92323 2.21034 9.62033 1.47757 11.3747 1.78691C13.4422 2.15146 14.9399 3.84879 15.1401 5.83952C15.1965 5.84687 15.253 5.85551 15.3096 5.86549C17.3492 6.22513 18.7111 8.1701 18.3514 10.2097C17.9918 12.2493 16.0468 13.6112 14.0072 13.2515C10.9243 12.7079 8.86587 12.345 5.39015 11.7321Z"
        stroke="#7F9FBE"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.5"
      />
    </svg>
  );
}

const weatherIcons: Record<WeatherIcon, () => React.JSX.Element> = {
  sun: SunIcon,
  cloud: CloudRainingIcon,
};

export interface DestinationCardProps {
  className?: string;
  destination: DestinationsCard & DestinationsCardPhotoTrait;
  favorited?: boolean;
  onFavoriteToggle?: () => void;
  /** Fires when the card is opened, before navigation. */
  onPress?: () => void;
  price?: string;
  /** How the price was arrived at. An estimate is drawn lighter than a
   *  measured figure and says so on hover, so the two never read alike. */
  priceGrade?: CostEvidenceGrade | null;
  /** Numeric rank displayed as a badge in the top-left corner of the card image. */
  rank?: number;
  /** The working behind that rank. When present the badge becomes the control
   *  that shows it — the only element on the card that already expresses
   *  ranking is the one that should answer for it. */
  rankExplanation?: RankingExplanation;
  showDescription?: boolean;
  temperature?: string;
  /** Visual treatment: "editorial" for spacious layouts, "compact" for dense grids. */
  theme?: CardTheme;
  weatherIcon?: WeatherIcon;
}

function WeatherTag({ icon, temperature }: { icon: WeatherIcon; temperature: string }) {
  const IconComponent = weatherIcons[icon];
  return <CardTag icon={<IconComponent />}>{temperature}</CardTag>;
}

function PriceTag({ grade, price }: { grade?: CostEvidenceGrade | null; price: string }) {
  const note = resolveDestinationPriceNote(grade);
  return (
    <span title={note}>
      <CardTag className={isEstimatedCost(grade) ? "px-3 font-medium" : "px-3"}>
        {price}
        <span className="sr-only">{note}</span>
      </CardTag>
    </span>
  );
}

// Static outside the composite-score sort, where a displayed ordinal would
// suggest a quality ranking the list is not in.
function rankBadge(rank?: number, explanation?: RankingExplanation) {
  if (rank == null) return null;
  if (!explanation) return <CardTag shape="circle">{rank}</CardTag>;

  return <RankExplanation explanation={explanation} rank={rank} />;
}

export function DestinationCard({
  destination,
  favorited,
  onFavoriteToggle,
  onPress,
  price,
  priceGrade,
  rank,
  rankExplanation,
  showDescription,
  temperature,
  theme = "editorial",
  weatherIcon,
}: DestinationCardProps) {
  const label = `${destination.name}, ${destination.country_name}`;
  const href = destinationPath(destination.country_slug, destination.slug);

  const heartOverlay = onFavoriteToggle ? (
    <CardFavoriteButton favorited={favorited} name={destination.name} onToggle={onFavoriteToggle} />
  ) : null;

  const bottomLeftOverlay =
    temperature && weatherIcon ? <WeatherTag icon={weatherIcon} temperature={temperature} /> : null;

  const bottomRightOverlay = price ? <PriceTag grade={priceGrade} price={price} /> : null;

  const topLeftOverlay = rankBadge(rank, rankExplanation);

  return (
    <Card className="group gap-2 lg:gap-2.5" href={href} linkLabel={label} onPress={onPress}>
      <CardImage
        alt={label}
        image={destination.photo}
        overlayBottomLeft={bottomLeftOverlay ?? undefined}
        overlayBottomRight={bottomRightOverlay ?? undefined}
        overlayInset="sm"
        overlayTopLeft={topLeftOverlay ?? undefined}
        overlayTopRight={heartOverlay ?? undefined}
      />
      <div className="flex flex-col gap-1">
        <CardTitle theme={theme}>{label}</CardTitle>
        {showDescription && destination.marketing_sentence ? (
          <CardDescription theme={theme}>{destination.marketing_sentence}</CardDescription>
        ) : null}
      </div>
    </Card>
  );
}
